import ApiService from './api.service'
import type { Firm, FirmUpdateData, FirmDashboardStats } from '@/types/services'

/**
 * Service for managing firm-related API endpoints
 */
class FirmService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Firm Error'
      }
    })
  }

  /**
   * Get dashboard statistics for the current firm
   * @returns Promise that resolves with the dashboard statistics
   */
  async getDashboardStats(): Promise<FirmDashboardStats | null> {
    return this.get('/api/firms/dashboard/stats')
  }

  /**
   * Get the currently authenticated user's firm
   * @returns Promise that resolves with the firm data
   */
  async getMyFirm(): Promise<Firm | null> {
    return this.get('/api/firms/mine')
  }

  /**
   * Update a firm with new data
   * @param id - The firm ID
   * @param firmData - The updated firm data
   * @returns Promise that resolves with the updated firm
   */
  async updateFirm(id: string | number, firmData: FirmUpdateData): Promise<Firm | null> {
    if (!id) {
      return Promise.reject({ message: 'No firm ID provided' })
    }
    
    return this.put(`/api/firms`, firmData)
  }

  /**
   * Upload a firm logo
   * @param firmId - The firm ID
   * @param file - The logo image file
   * @returns Promise that resolves with the logo URL
   */
  async uploadLogo(file: File): Promise<{ logoUrl: string } | null> {
    if (!file) {
      return Promise.reject({ message: 'File is required' })
    }
    
    const formData = new FormData()
    formData.append('logo', file)

    return this.post(`/api/firms/logo`, formData)
  }

  /**
   * Get all intervenants for all projects belonging to the authenticated user's firm
   * Note: firmId parameter is kept for backward compatibility but not used in the API call
   * The backend determines the firm ID from the authenticated user's context
   * @param firmId - The firm ID (not used, kept for compatibility)
   * @returns Promise that resolves with the list of intervenants
   */
  async getFirmIntervenants(firmId?: string | number): Promise<any[] | null> {
    return this.get('/api/firms/intervenants')
  }

  /**
   * Get paginated intervenants for all projects belonging to the authenticated user's firm
   * Note: firmId parameter is kept for backward compatibility but not used in the API call
   * The backend determines the firm ID from the authenticated user's context
   * @param firmId - The firm ID (not used, kept for compatibility)
   * @param page - Page number (0-based)
   * @param size - Page size
   * @returns Promise that resolves with paginated intervenants
   */
  async getFirmIntervenantsPaginated(
    firmId?: string | number,
    page: number = 0,
    size: number = 20
  ): Promise<any | null> {
    return this.get('/api/firms/intervenants/paginated', {
      page,
      size
    })
  }
}

// Create a singleton instance
const firmService = new FirmService()

// Export the singleton
export default firmService
